import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { PreferredSportProvider } from "@/hooks/usePreferredSport";
import { SportSkillProvider } from "@/hooks/useSportSkillLevel";
import { LandingPage } from "@/components/LandingPage";
import { ContactPage } from "@/components/ContactPage";
import { useState, useEffect, useRef } from "react";
import Index from "./pages/Index.tsx";
import Auth from "./pages/Auth.tsx";
import NotFound from "./pages/NotFound.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import InvitePage from "./pages/InvitePage.tsx";
import JoinPage from "./pages/JoinPage.tsx";
import CoachDashboard from "./pages/CoachDashboard.tsx";
import CoachesLanding from "./pages/CoachesLanding.tsx";
import { lazy, Suspense } from "react";
const LessonPreview = lazy(() => import("./pages/LessonPreview.tsx"));
const TryLesson = lazy(() => import("./pages/TryLesson.tsx"));
import { TermsOfService } from "./pages/TermsOfService.tsx";
import { PrivacyPolicy } from "./pages/PrivacyPolicy.tsx";
import FootballRulesBeginners from "./pages/guides/FootballRulesBeginners.tsx";
import { supabase } from "@/integrations/supabase/client";
import { useViewMode } from "@/hooks/useViewMode";
import { CoachSignupFlow, COACH_SIGNUP_ACTIVE_KEY } from "@/components/auth/CoachSignupFlow";
import { ParentSignupFlow, PARENT_SIGNUP_ACTIVE_KEY } from "@/components/auth/ParentSignupFlow";
import { pendingPlayerLoginUsername } from "@/lib/switchToChild";



const queryClient = new QueryClient();

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingPlayerLogin = !!pendingPlayerLoginUsername();
  const [showAuth, setShowAuth] = useState(pendingPlayerLogin);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'player'>(
    pendingPlayerLogin ? 'player' : 'login',
  );
  // Coach CTAs on the landing page skip the role-choice screen and land
  // directly on the coach signup flow.
  const [authSignupPath, setAuthSignupPath] = useState<'coach' | null>(null);

  const [isCoach, setIsCoach] = useState<boolean | null>(null);
  const [isPlayerAccount, setIsPlayerAccount] = useState<boolean | null>(null);
  const [primaryRole, setPrimaryRole] = useState<string | null>(null);
  // null = unknown/not applicable. false = coach account with no team yet
  // (onboarding never finished), which must resume the guided coach flow.
  const [coachHasTeam, setCoachHasTeam] = useState<boolean | null>(null);
  // Same idea for parents: false = parent account that never created a child,
  // so the guided parent flow must resume instead of the player onboarding.
  const [parentHasChild, setParentHasChild] = useState<boolean | null>(null);
  // Coach-flow signups default to the coach view once per session; after that
  // the Player/Coach toggle wins.
  const roleDefaultApplied = useRef(false);
  // Default dual-profile users to player on first login; remembered after that.
  const { mode, setMode } = useViewMode('player');

  // Check coach + child-player status in parallel
  useEffect(() => {
    if (!user) {
      setIsCoach(null); setIsPlayerAccount(null); setPrimaryRole(null);
      setCoachHasTeam(null); setParentHasChild(null); return;
    }
    let cancelled = false;
    Promise.all([
      supabase.from('coach_profiles').select('id, onboarding_completed_at').eq('user_id', user.id).maybeSingle(),
      (supabase.from('player_profiles' as any) as any).select('id').eq('player_user_id', user.id).maybeSingle(),
      supabase.from('profiles').select('primary_role').eq('user_id', user.id).maybeSingle(),
      (supabase.from('player_profiles' as any) as any).select('id').eq('parent_user_id', user.id).limit(1),
    ]).then(([coachRes, playerRes, profileRes, childrenRes]: any[]) => {
      if (cancelled) return;
      setIsCoach(!!coachRes.data);
      setIsPlayerAccount(!!playerRes.data);
      const role = (profileRes?.data as any)?.primary_role ?? 'player';
      setPrimaryRole(role);
      setParentHasChild(
        role === 'parent'
          ? !!(childrenRes?.data && childrenRes.data.length > 0)
          : null
      );
      roleDefaultApplied.current = false;
      const coachProfileId = (coachRes?.data as any)?.id;
      if (!coachProfileId) { setCoachHasTeam(null); return; }
      // Wizard finished once → never resume it, even if a stale tab flag remains.
      if ((coachRes?.data as any)?.onboarding_completed_at) {
        sessionStorage.removeItem(COACH_SIGNUP_ACTIVE_KEY);
        setCoachHasTeam(true);
        return;
      }
      supabase
        .from('teams')
        .select('id')
        .eq('coach_id', coachProfileId)
        .limit(1)
        .then(({ data }) => { if (!cancelled) setCoachHasTeam(!!data && data.length > 0); });
    });
    return () => { cancelled = true; };
  }, [user]);

  // Coerce mode based on role so the default view always matches the user.
  useEffect(() => {
    if (isCoach === null || isPlayerAccount === null) return;
    // Child player accounts can never be coaches.
    if (isPlayerAccount && mode !== 'player') { setMode('player'); return; }
    // No coach profile → always player.
    if (!isCoach && mode !== 'player') { setMode('player'); return; }
    // Signed up through the coach flow → always land on the coach dashboard.
    if (roleDefaultApplied.current) return;
    roleDefaultApplied.current = true;
    if (isCoach && primaryRole === 'coach' && mode !== 'coach') { setMode('coach'); }
  }, [isCoach, isPlayerAccount, primaryRole, mode, setMode]);

  useEffect(() => {
    const code = sessionStorage.getItem('sportara-pending-team-code');
    if (user && primaryRole && primaryRole !== 'parent' && primaryRole !== 'coach' && code && !location.pathname.startsWith('/join')) {
      navigate(`/join/${encodeURIComponent(code)}`, { replace: true });
    }
  }, [user, primaryRole, location.pathname, navigate]);

  const coachOnboardingIncomplete =
    !!user && !!isCoach && primaryRole === 'coach' && coachHasTeam === false;
  const parentOnboardingIncomplete =
    !!user && primaryRole === 'parent' && parentHasChild === false;

  // Keep the coach dashboard at its canonical URL without changing the
  // existing player/coach view toggle or interrupting either signup wizard.
  const showCoach = !!user && !!isCoach && !isPlayerAccount && mode === 'coach';
  useEffect(() => {
    if (!user || isCoach === null || isPlayerAccount === null || primaryRole === null ||
        coachOnboardingIncomplete || parentOnboardingIncomplete ||
        sessionStorage.getItem(COACH_SIGNUP_ACTIVE_KEY) === '1' ||
        sessionStorage.getItem(PARENT_SIGNUP_ACTIVE_KEY) === '1') return;
    if (showCoach && (location.pathname === '/' || location.pathname === '/coaches')) navigate('/coaches/dashboard', { replace: true });
    if (!showCoach && location.pathname === '/coaches/dashboard') navigate('/', { replace: true });
  }, [user, isCoach, isPlayerAccount, primaryRole, coachOnboardingIncomplete, parentOnboardingIncomplete, showCoach, location.pathname, navigate]);

  if (loading || (user && (isCoach === null || isPlayerAccount === null || primaryRole === null)) ||
      (!!user && primaryRole === 'parent' && parentHasChild === null) ||
      (!!user && !!isCoach && coachHasTeam === null && primaryRole === 'coach')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
            <div className="w-5 h-5 rounded-full bg-primary/40" />
          </div>
          <p className="font-display font-bold text-lg mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (location.pathname === '/coach') return <Navigate to={user ? '/coaches/dashboard' : '/coaches'} replace />;

  // Parent invite emails link to /parent/signup?team=CODE&child=NAME. Stash both
  // before the parent wizard mounts so its fields are pre-filled.
  if (location.pathname === '/parent/signup') {
    const params = new URLSearchParams(location.search);
    const team = (params.get('team') || '').replace(/[^a-z0-9]/gi, '').slice(0, 20);
    const child = (params.get('child') || '').replace(/[<>]/g, '').trim().slice(0, 40);
    if (team) sessionStorage.setItem('sportara-pending-team-code', team.toUpperCase());
    if (child) sessionStorage.setItem('sportara-pending-child-name', child);
  }

  // A coach mid-signup must stay in the guided flow (team + code come later),
  // never get bounced onto the dashboard the moment their account exists.
  if (user && location.pathname !== '/contact' && location.pathname !== '/terms' && location.pathname !== '/privacy' && !location.pathname.startsWith('/join') && (sessionStorage.getItem(COACH_SIGNUP_ACTIVE_KEY) === '1' || coachOnboardingIncomplete)) {
    return (
      <CoachSignupFlow
        onExit={() => {
          sessionStorage.removeItem(COACH_SIGNUP_ACTIVE_KEY);
          window.location.href = '/';
        }}
      />
    );
  }

  // Same guard for the parent flow — the child profile, team code, and
  // preferences screens all come after the parent account exists.
  if (user && location.pathname !== '/contact' && location.pathname !== '/terms' && location.pathname !== '/privacy' && !location.pathname.startsWith('/join') && (sessionStorage.getItem(PARENT_SIGNUP_ACTIVE_KEY) === '1' || parentOnboardingIncomplete)) {
    return (
      <ParentSignupFlow
        onExit={() => {
          sessionStorage.removeItem(PARENT_SIGNUP_ACTIVE_KEY);
          window.location.href = '/';
        }}
      />
    );
  }


  // `key` forces a full unmount/remount on mode swap so any open modals,
  // overlays, or banners (e.g. Weekly Recap) from the previous view are gone.
  const mainElement = user ? (
    location.pathname.startsWith('/auth') ? <Navigate to="/" replace /> : (
    <div key={showCoach ? 'coach' : 'player'} className="animate-fade-in">
      {showCoach ? <CoachDashboard /> : <Index />}
    </div>)
  ) : (location.pathname.startsWith('/auth') || location.pathname === '/parent/signup' || showAuth) ? (

    <Auth
      onBack={() => { setShowAuth(false); navigate('/'); }}
      initialMode={(location.pathname.includes('signup') || location.pathname === '/auth/coach' || location.pathname === '/auth/parent' || location.pathname === '/auth/player') ? 'signup' : authMode}
      initialSignupPath={location.pathname === '/auth/coach' ? 'coach' : (location.pathname === '/auth/parent' || location.pathname === '/parent/signup') ? 'parent' : location.pathname === '/auth/player' ? 'player' : authSignupPath ?? undefined}
    />
  ) : (
    <LandingPage
      onGetStarted={() => navigate('/get-started')}
      onSignIn={() => navigate('/auth')}
      onContact={() => navigate('/contact')}
      onTerms={() => navigate('/terms')}
      onPrivacy={() => navigate('/privacy')}
      onCoachSignup={() => navigate('/auth/coach')}
    />
  );

  return (
    <main>
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invite/:code" element={<InvitePage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/join/:code" element={<JoinPage />} />
        <Route path="/get-started" element={<Navigate to="/auth/signup" replace />} />
        <Route path="/auth" element={mainElement} />
        <Route path="/auth/signup" element={mainElement} />
        <Route path="/auth/coach" element={mainElement} />
        <Route path="/auth/parent" element={mainElement} />
        <Route path="/auth/player" element={mainElement} />
        <Route path="/parent/signup" element={mainElement} />
        <Route path="/coach" element={<Navigate to={user ? '/coaches/dashboard' : '/coaches'} replace />} />
        <Route path="/coaches" element={user && showCoach ? mainElement : <CoachesLanding />} />
        <Route path="/coaches/dashboard" element={user ? mainElement : <Navigate to="/auth" replace />} />
        <Route path="/contact" element={<ContactPage onBack={() => navigate(-1)} />} />
        <Route path="/terms" element={<TermsOfService onBack={() => navigate(-1)} />} />
        <Route path="/privacy" element={<PrivacyPolicy onBack={() => navigate(-1)} />} />
        <Route path="/try" element={<Suspense fallback={null}><TryLesson /></Suspense>} />
        <Route path="/learn/:sport/:slug" element={<Suspense fallback={null}><LessonPreview /></Suspense>} />
        <Route path="/guides/football-rules-beginners" element={<FootballRulesBeginners />} />
        <Route path="/" element={mainElement} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
  );
}


import { ThemeProvider } from "next-themes";

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} storageKey="sportara-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AuthProvider>
            <PreferredSportProvider>
              <SportSkillProvider>
                <BrowserRouter>
                  <AppRoutes />
                </BrowserRouter>
              </SportSkillProvider>
            </PreferredSportProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
